import React, { useState, useEffect } from 'react';
import { Bug, FileText, Lightbulb, FlaskConical, Plus, X, ChevronDown, Search, RefreshCw, Trash2 } from 'lucide-react';
import api from '../services/api';

// Types
interface BugReport {
  id: number;
  title: string;
  description: string;
  status: string;
  severity: string;
  category: string;
  affected_area?: string;
  affected_files?: string;
  root_cause?: string;
  fix_applied?: string;
  reported_by?: string;
  created_at: string;
  resolved_at?: string;
}

interface Feature {
  id: number;
  name: string;
  description: string;
  expected_behavior: string;
  category?: string;
  status: string;
  api_endpoints?: string;
  frontend_pages?: string;
  key_files?: string;
  dependencies?: string;
  created_at: string;
}

interface Research {
  id: number;
  title: string;
  summary: string;
  findings: string;
  category: string;
  area?: string;
  data_evidence?: string;
  methodology?: string;
  conclusion?: string;
  action_taken?: string;
  status: string;
  affects_production: boolean;
  tags?: string;
  researcher?: string;
  research_date: string;
}

interface Idea {
  id: number;
  title: string;
  summary: string;
  category: string;
  area?: string;
  problem?: string;
  proposed_solution?: string;
  expected_benefit?: string;
  priority: string;
  effort?: string;
  impact?: string;
  status: string;
  tags?: string;
  proposed_by?: string;
  created_at: string;
}

// Status badge component
const StatusBadge: React.FC<{ status: string; type?: string }> = ({ status, type }) => {
  const getColor = () => {
    if (type === 'severity') {
      switch (status) {
        case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
        case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      }
    }
    if (type === 'priority') {
      switch (status) {
        case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
        case 'medium': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        case 'low': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
        case 'someday': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      }
    }
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'investigating': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'fixed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'validated': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'proposed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'implemented': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'deprecated': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getColor()}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

// Tab definitions
const tabs = [
  { id: 'bugs', label: 'Bugs', icon: Bug },
  { id: 'features', label: 'Features', icon: FileText },
  { id: 'research', label: 'Research', icon: FlaskConical },
  { id: 'ideas', label: 'Ideas', icon: Lightbulb },
];

const Tracking: React.FC = () => {
  const [activeTab, setActiveTab] = useState('bugs');
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [research, setResearch] = useState<Research[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<BugReport | Feature | Research | Idea | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Form states
  const [bugForm, setBugForm] = useState({ title: '', description: '', severity: 'medium', category: 'other', affected_area: '', affected_files: '' });
  const [featureForm, setFeatureForm] = useState({ name: '', description: '', expected_behavior: '', category: 'other', key_files: '', api_endpoints: '' });
  const [researchForm, setResearchForm] = useState({ title: '', summary: '', findings: '', category: 'discovery', area: '', affects_production: false });
  const [ideaForm, setIdeaForm] = useState({ title: '', summary: '', category: 'other', priority: 'medium', problem: '', proposed_solution: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bugsData, featuresData, researchData, ideasData] = await Promise.all([
        api.getBugs(),
        api.getFeatures(),
        api.getResearch(),
        api.getIdeas(),
      ]);

      setBugs(bugsData || []);
      setFeatures(featuresData || []);
      setResearch(researchData || []);
      setIdeas(ideasData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      switch (activeTab) {
        case 'bugs':
          await api.createBug({ ...bugForm, reported_by: 'digimint-ui', status: 'open' });
          setBugForm({ title: '', description: '', severity: 'medium', category: 'other', affected_area: '', affected_files: '' });
          break;
        case 'features':
          await api.createFeature({ ...featureForm, status: 'active' });
          setFeatureForm({ name: '', description: '', expected_behavior: '', category: 'other', key_files: '', api_endpoints: '' });
          break;
        case 'research':
          await api.createResearch({ ...researchForm, researcher: 'digimint-ui', status: 'pending' });
          setResearchForm({ title: '', summary: '', findings: '', category: 'discovery', area: '', affects_production: false });
          break;
        case 'ideas':
          await api.createIdea({ ...ideaForm, proposed_by: 'digimint-ui', status: 'proposed' });
          setIdeaForm({ title: '', summary: '', category: 'other', priority: 'medium', problem: '', proposed_solution: '' });
          break;
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error creating item:', error);
    }
  };

  const updateStatus = async (type: string, id: number, status: string) => {
    try {
      switch (type) {
        case 'bugs':
          await api.updateBug(id, { status });
          break;
        case 'features':
          await api.updateFeature(id, { status });
          break;
        case 'research':
          await api.updateResearch(id, { status });
          break;
        case 'ideas':
          await api.updateIdea(id, { status });
          break;
      }
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteItem = async (type: string, id: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      switch (type) {
        case 'bugs':
          await api.deleteBug(id);
          break;
        case 'features':
          await api.deleteFeature(id);
          break;
        case 'research':
          await api.deleteResearch(id);
          break;
        case 'ideas':
          await api.deleteIdea(id);
          break;
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const filterItems = <T extends { title?: string; name?: string; description?: string; summary?: string }>(
    items: T[]
  ): T[] => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      (item.title?.toLowerCase().includes(query)) ||
      (item.name?.toLowerCase().includes(query)) ||
      (item.description?.toLowerCase().includes(query)) ||
      (item.summary?.toLowerCase().includes(query))
    );
  };

  const renderBugs = () => (
    <div className="space-y-4">
      {filterItems(bugs).map(bug => (
        <div key={bug.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1 cursor-pointer" onClick={() => { setSelectedItem(bug); setShowDetail(true); }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">#{bug.id}</span>
                <h3 className="font-semibold text-gray-900 dark:text-white">{bug.title}</h3>
                <StatusBadge status={bug.status} />
                <StatusBadge status={bug.severity} type="severity" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{bug.description}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                <span>Category: {bug.category}</span>
                {bug.affected_area && <span>Area: {bug.affected_area}</span>}
                <span>Reported: {formatDate(bug.created_at)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative group">
                <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hidden group-hover:block z-10">
                  <button onClick={() => updateStatus('bugs', bug.id, 'investigating')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Investigating</button>
                  <button onClick={() => updateStatus('bugs', bug.id, 'fixed')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Mark Fixed</button>
                  <button onClick={() => updateStatus('bugs', bug.id, 'wont_fix')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Won't Fix</button>
                </div>
              </div>
              <button onClick={() => deleteItem('bugs', bug.id)} className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
      {filterItems(bugs).length === 0 && (
        <div className="text-center py-3 text-gray-500 dark:text-gray-400">No bugs found</div>
      )}
    </div>
  );

  const renderFeatures = () => (
    <div className="space-y-4">
      {filterItems(features).map(feature => (
        <div key={feature.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1 cursor-pointer" onClick={() => { setSelectedItem(feature); setShowDetail(true); }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">#{feature.id}</span>
                <h3 className="font-semibold text-gray-900 dark:text-white">{feature.name}</h3>
                <StatusBadge status={feature.status} />
                {feature.category && <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">{feature.category}</span>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{feature.description}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                {feature.key_files && <span>Files: {feature.key_files.substring(0, 50)}...</span>}
                <span>Created: {formatDate(feature.created_at)}</span>
              </div>
            </div>
            <button onClick={() => deleteItem('features', feature.id)} className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg text-red-500">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      {filterItems(features).length === 0 && (
        <div className="text-center py-3 text-gray-500 dark:text-gray-400">No features documented</div>
      )}
    </div>
  );

  const renderResearch = () => (
    <div className="space-y-4">
      {filterItems(research).map(item => (
        <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1 cursor-pointer" onClick={() => { setSelectedItem(item); setShowDetail(true); }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">#{item.id}</span>
                <h3 className="font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                <StatusBadge status={item.status} />
                <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">{item.category.replace('_', ' ')}</span>
                {item.affects_production && <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Production</span>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{item.summary}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                {item.area && <span>Area: {item.area}</span>}
                {item.researcher && <span>By: {item.researcher}</span>}
                <span>Date: {formatDate(item.research_date)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative group">
                <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hidden group-hover:block z-10">
                  <button onClick={() => updateStatus('research', item.id, 'validated')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Validate</button>
                  <button onClick={() => updateStatus('research', item.id, 'invalidated')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Invalidate</button>
                  <button onClick={() => updateStatus('research', item.id, 'archived')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Archive</button>
                </div>
              </div>
              <button onClick={() => deleteItem('research', item.id)} className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
      {filterItems(research).length === 0 && (
        <div className="text-center py-3 text-gray-500 dark:text-gray-400">No research entries</div>
      )}
    </div>
  );

  const renderIdeas = () => (
    <div className="space-y-4">
      {filterItems(ideas).map(idea => (
        <div key={idea.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1 cursor-pointer" onClick={() => { setSelectedItem(idea); setShowDetail(true); }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">#{idea.id}</span>
                <h3 className="font-semibold text-gray-900 dark:text-white">{idea.title}</h3>
                <StatusBadge status={idea.status} />
                <StatusBadge status={idea.priority} type="priority" />
                <span className="px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">{idea.category}</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{idea.summary}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                {idea.area && <span>Area: {idea.area}</span>}
                {idea.proposed_by && <span>By: {idea.proposed_by}</span>}
                <span>Created: {formatDate(idea.created_at)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative group">
                <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hidden group-hover:block z-10">
                  <button onClick={() => updateStatus('ideas', idea.id, 'approved')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Approve</button>
                  <button onClick={() => updateStatus('ideas', idea.id, 'in_progress')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">In Progress</button>
                  <button onClick={() => updateStatus('ideas', idea.id, 'implemented')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Implemented</button>
                  <button onClick={() => updateStatus('ideas', idea.id, 'rejected')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Reject</button>
                  <button onClick={() => updateStatus('ideas', idea.id, 'deferred')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Defer</button>
                </div>
              </div>
              <button onClick={() => deleteItem('ideas', idea.id)} className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
      {filterItems(ideas).length === 0 && (
        <div className="text-center py-3 text-gray-500 dark:text-gray-400">No ideas yet</div>
      )}
    </div>
  );

  const renderForm = () => {
    switch (activeTab) {
      case 'bugs':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
              <input type="text" value={bugForm.title} onChange={e => setBugForm({...bugForm, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
              <textarea value={bugForm.description} onChange={e => setBugForm({...bugForm, description: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Severity</label>
                <select value={bugForm.severity} onChange={e => setBugForm({...bugForm, severity: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select value={bugForm.category} onChange={e => setBugForm({...bugForm, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="google_ads">Google Ads</option>
                  <option value="rules_engine">Rules Engine</option>
                  <option value="api">API</option>
                  <option value="ui">UI</option>
                  <option value="data_quality">Data Quality</option>
                  <option value="performance">Performance</option>
                  <option value="auth">Auth</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Affected Area</label>
                <input type="text" value={bugForm.affected_area} onChange={e => setBugForm({...bugForm, affected_area: e.target.value})} placeholder="e.g., optimization, audit" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Affected Files</label>
                <input type="text" value={bugForm.affected_files} onChange={e => setBugForm({...bugForm, affected_files: e.target.value})} placeholder="e.g., views.py, api.ts" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
            </div>
          </>
        );
      case 'features':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
              <input type="text" value={featureForm.name} onChange={e => setFeatureForm({...featureForm, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
              <textarea value={featureForm.description} onChange={e => setFeatureForm({...featureForm, description: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Behavior *</label>
              <textarea value={featureForm.expected_behavior} onChange={e => setFeatureForm({...featureForm, expected_behavior: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select value={featureForm.category} onChange={e => setFeatureForm({...featureForm, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="google_ads">Google Ads</option>
                <option value="rules_engine">Rules Engine</option>
                <option value="algorithms">Algorithms</option>
                <option value="admin">Admin</option>
                <option value="data_import">Data Import</option>
                <option value="ui">UI</option>
                <option value="api">API</option>
                <option value="infrastructure">Infrastructure</option>
                <option value="auth">Auth</option>
                <option value="billing">Billing</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key Files</label>
                <input type="text" value={featureForm.key_files} onChange={e => setFeatureForm({...featureForm, key_files: e.target.value})} placeholder="e.g., views.py, api.ts" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Endpoints</label>
                <input type="text" value={featureForm.api_endpoints} onChange={e => setFeatureForm({...featureForm, api_endpoints: e.target.value})} placeholder="e.g., /api/v1/tracking/" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
            </div>
          </>
        );
      case 'research':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
              <input type="text" value={researchForm.title} onChange={e => setResearchForm({...researchForm, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary *</label>
              <textarea value={researchForm.summary} onChange={e => setResearchForm({...researchForm, summary: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Findings *</label>
              <textarea value={researchForm.findings} onChange={e => setResearchForm({...researchForm, findings: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select value={researchForm.category} onChange={e => setResearchForm({...researchForm, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="algorithm_tuning">Algorithm Tuning</option>
                  <option value="data_analysis">Data Analysis</option>
                  <option value="experiment">Experiment</option>
                  <option value="discovery">Discovery</option>
                  <option value="hypothesis">Hypothesis</option>
                  <option value="calibration">Calibration</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Area</label>
                <input type="text" value={researchForm.area} onChange={e => setResearchForm({...researchForm, area: e.target.value})} placeholder="e.g., google_ads, optimization" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="affects_production" checked={researchForm.affects_production} onChange={e => setResearchForm({...researchForm, affects_production: e.target.checked})} className="w-4 h-4" />
              <label htmlFor="affects_production" className="text-sm text-gray-700 dark:text-gray-300">Affects Production</label>
            </div>
          </>
        );
      case 'ideas':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
              <input type="text" value={ideaForm.title} onChange={e => setIdeaForm({...ideaForm, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary *</label>
              <textarea value={ideaForm.summary} onChange={e => setIdeaForm({...ideaForm, summary: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select value={ideaForm.category} onChange={e => setIdeaForm({...ideaForm, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="google_ads">Google Ads</option>
                  <option value="rules_engine">Rules Engine</option>
                  <option value="algorithm">Algorithm</option>
                  <option value="ui">UI</option>
                  <option value="performance">Performance</option>
                  <option value="data_quality">Data Quality</option>
                  <option value="ux">UX</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="integration">Integration</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                <select value={ideaForm.priority} onChange={e => setIdeaForm({...ideaForm, priority: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="someday">Someday</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Problem to Solve</label>
              <textarea value={ideaForm.problem} onChange={e => setIdeaForm({...ideaForm, problem: e.target.value})} rows={2} placeholder="What problem does this solve?" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Proposed Solution</label>
              <textarea value={ideaForm.proposed_solution} onChange={e => setIdeaForm({...ideaForm, proposed_solution: e.target.value})} rows={2} placeholder="How could this be implemented?" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
          </>
        );
    }
  };

  return (
    <div className="p-3 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-base font-bold text-gray-900 dark:text-white">Tracking</h1>
          <p className="text-gray-500 dark:text-gray-400">Manage bugs, features, research, and ideas</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchData} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Add New</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <Bug className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-base font-bold text-gray-900 dark:text-white">{bugs.filter(b => !['fixed', 'wont_fix'].includes(b.status)).length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Open Bugs</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-base font-bold text-gray-900 dark:text-white">{features.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Features</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <FlaskConical className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-base font-bold text-gray-900 dark:text-white">{research.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Research</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <Lightbulb className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-base font-bold text-gray-900 dark:text-white">{ideas.filter(i => ['proposed', 'approved', 'in_progress'].includes(i.status)).length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Open Ideas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-5 h-5 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {activeTab === 'bugs' && renderBugs()}
          {activeTab === 'features' && renderFeatures()}
          {activeTab === 'research' && renderResearch()}
          {activeTab === 'ideas' && renderIdeas()}
        </>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {renderForm()}
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {'title' in selectedItem ? selectedItem.title : (selectedItem as Feature).name}
              </h2>
              <button onClick={() => { setShowDetail(false); setSelectedItem(null); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <pre className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto">
                {JSON.stringify(selectedItem, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracking;
